import { DestroyRef, Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import { environment } from '@environments/environment';

interface ResolvedPhotoUrl {
  url: string;
  requiresAuthentication: boolean;
  responseKind: 'metadata' | 'binary';
}

interface EquipmentPhotoPayload {
  id?: number;
  nome?: string;
  descricao?: string;
  url?: string | null;
  mimeType?: string | null;
  extensao?: string | null;
  file?: string | null;
  data?: EquipmentPhotoPayload;
}

@Injectable({ providedIn: 'root' })
export class EquipmentPhotoService {
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cache = new Map<string, Observable<string | null>>();
  private readonly objectUrls = new Map<string, string>();

  constructor() {
    this.destroyRef.onDestroy(() => this.clear());
  }

  resolve(rawUrl?: string | null): Observable<string | null> {
    if (!rawUrl) return of(null);

    const resolved = this.normalize(rawUrl);
    if (!resolved.requiresAuthentication) return of(resolved.url);

    const cached = this.cache.get(resolved.url);
    if (cached) return cached;

    const photoRequest$ =
      resolved.responseKind === 'binary'
        ? this.http
            .get(resolved.url, {
              responseType: 'arraybuffer',
              headers: { Accept: 'image/*' },
            })
            .pipe(
              map((response) =>
                this.createBinaryPhotoObjectUrl(resolved.url, response),
              ),
            )
        : this.http
            .get<EquipmentPhotoPayload>(resolved.url)
            .pipe(
              map((response) =>
                this.createPhotoObjectUrl(resolved.url, response),
              ),
            );

    const request$ = photoRequest$.pipe(
      catchError(() => {
        this.cache.delete(resolved.url);
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.cache.set(resolved.url, request$);
    return request$;
  }

  clear(rawUrl?: string | null): void {
    if (!rawUrl) {
      this.objectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
      this.objectUrls.clear();
      this.cache.clear();
      return;
    }

    const normalizedUrl = this.normalize(rawUrl).url;
    const objectUrl = this.objectUrls.get(normalizedUrl);
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    this.objectUrls.delete(normalizedUrl);
    this.cache.delete(normalizedUrl);
  }

  private createBinaryPhotoObjectUrl(
    sourceUrl: string,
    response: ArrayBuffer,
  ): string | null {
    const bytes = new Uint8Array(response);
    if (!bytes.length) return null;

    const mimeType = this.detectMimeType(bytes);
    if (!mimeType) return null;

    const objectUrl = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
    this.objectUrls.set(sourceUrl, objectUrl);
    return objectUrl;
  }

  private createPhotoObjectUrl(
    sourceUrl: string,
    response: EquipmentPhotoPayload,
  ): string | null {
    const payload = response?.data ?? response;
    if (!payload?.file) return null;

    const encodedFile = payload.file.includes(',')
      ? payload.file.slice(payload.file.indexOf(',') + 1)
      : payload.file;
    const normalizedBase64 = encodedFile.replace(/\s/g, '');
    if (!normalizedBase64) return null;

    const binary = atob(normalizedBase64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index++) {
      bytes[index] = binary.charCodeAt(index);
    }

    const mimeType =
      this.detectMimeType(bytes) ||
      payload.mimeType?.trim() ||
      this.getMimeTypeFromExtension(payload.extensao) ||
      'image/jpeg';
    const objectUrl = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
    this.objectUrls.set(sourceUrl, objectUrl);
    return objectUrl;
  }

  private detectMimeType(bytes: Uint8Array): string | null {
    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      return 'image/jpeg';
    }

    if (
      bytes.length >= 8 &&
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47 &&
      bytes[4] === 0x0d &&
      bytes[5] === 0x0a &&
      bytes[6] === 0x1a &&
      bytes[7] === 0x0a
    ) {
      return 'image/png';
    }

    const signature = String.fromCharCode(...bytes.slice(0, 12));
    if (signature.startsWith('GIF87a') || signature.startsWith('GIF89a')) {
      return 'image/gif';
    }
    if (signature.startsWith('RIFF') && signature.slice(8, 12) === 'WEBP') {
      return 'image/webp';
    }
    if (signature.slice(4, 12).startsWith('ftypavif')) {
      return 'image/avif';
    }

    const textStart = new TextDecoder().decode(bytes.slice(0, 256)).trimStart();
    return textStart.startsWith('<svg') || textStart.startsWith('<?xml')
      ? 'image/svg+xml'
      : null;
  }

  private getMimeTypeFromExtension(extension?: string | null): string | null {
    const normalized = extension?.replace('.', '').trim().toLowerCase();
    const mimeTypes: Record<string, string> = {
      avif: 'image/avif',
      gif: 'image/gif',
      jpeg: 'image/jpeg',
      jpg: 'image/jpeg',
      png: 'image/png',
      svg: 'image/svg+xml',
      webp: 'image/webp',
    };
    return normalized ? mimeTypes[normalized] ?? null : null;
  }

  private normalize(rawUrl: string): ResolvedPhotoUrl {
    if (rawUrl.startsWith('blob:') || rawUrl.startsWith('data:')) {
      return {
        url: rawUrl,
        requiresAuthentication: false,
        responseKind: 'binary',
      };
    }

    const apiUrl = new URL(environment.apiurl);
    const candidate = new URL(rawUrl, apiUrl.origin);
    const normalizedPath = candidate.pathname.toLowerCase();
    // Apenas os endpoints de anexos precisam de ser descarregados pelo
    // HttpClient (com autenticação). Assets públicos, como o logo padrão,
    // devem ser carregados directamente pelo elemento <img>; caso contrário
    // o browser bloqueia o pedido por CORS.
    const isApiAttachment = normalizedPath.startsWith('/api/anexos/');
    const isBinaryEndpoint =
      normalizedPath.includes('/api/anexos/preview/') ||
      normalizedPath.includes('/api/anexos/download/');

    if (isApiAttachment) {
      return {
        url: apiUrl.origin + candidate.pathname + candidate.search,
        requiresAuthentication: true,
        responseKind: isBinaryEndpoint ? 'binary' : 'metadata',
      };
    }

    return {
      url: candidate.toString(),
      requiresAuthentication: false,
      responseKind: 'binary',
    };
  }
}
